import { useId, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { CommissionPlan } from '@/api/commissionPlans';
import type { AffiliateReferralRow } from '@/api/generated';
import { useToast } from '@/components/useToast';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useCanWrite } from '@/auth/authHooks';
import { MoneyCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import { formatCivilOrDash } from '@/i18n/format';
import { enumLabel } from '@/i18n/enumLabel';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { VoidReferralDialog } from './VoidReferralDialog';
import { firstPaidOnOf, referralPlanIdOf } from './affiliatePlan';

export interface ReferralsTableProps {
  referrals: AffiliateReferralRow[];
  /** The reseller's plans, which name each referral's plan; `null` while not loaded. */
  plans: CommissionPlan[] | null;
  onVoid: (attributionId: number, reason: string) => Promise<void>;
}

function isVoided(row: AffiliateReferralRow): boolean {
  return row.status === 'VOIDED';
}

export function ReferralsTable({ referrals, plans, onVoid }: ReferralsTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const showToast = useToast();
  const canWrite = useCanWrite();
  const headingId = useId();
  const [target, setTarget] = useState<AffiliateReferralRow | null>(null);
  const dash = t('affiliates.unavailable');

  function planLabel(id: number): string {
    // Not loaded (or not loadable): the dash, never a guess (TXT-006, TMMRSL-0073).
    if (!plans) return dash;
    const plan = plans.find((candidate) => candidate.id === id);
    if (plan === undefined) return t('affiliates.referrals.planUnlisted');
    return plan.name || t('commissionPlans.unnamed');
  }

  async function handleVoid(reason: string) {
    if (target?.attributionId === undefined) return;
    await onVoid(target.attributionId, reason);
    showToast(t('affiliates.toast.voided'));
  }

  return (
    <Paper sx={{ p: 2.5, minWidth: 0 }}>
      <Typography variant="h3" component="h3" id={headingId}>
        {t('affiliates.referrals.title')}
      </Typography>
      {referrals.length === 0 ? (
        <Typography variant="body2" sx={{ color: tokens.text2, mt: 1 }}>
          {t('affiliates.referrals.empty')}
        </Typography>
      ) : (
        <Table size="small" sx={{ mt: 1 }} aria-labelledby={headingId}>
          <TableHead>
            <TableRow>
              <TableCell>{t('affiliates.referrals.subscriber')}</TableCell>
              <TableCell>{t('affiliates.referrals.offer')}</TableCell>
              <TableCell>{t('affiliates.referrals.since')}</TableCell>
              <TableCell>{t('affiliates.referrals.firstPaid')}</TableCell>
              <TableCell>{t('affiliates.referrals.statusLabel')}</TableCell>
              <TableCell>{t('affiliates.referrals.source')}</TableCell>
              <TableCell>{t('affiliates.referrals.plan')}</TableCell>
              <TableCell align="right">
                {t('affiliates.referrals.period')}
              </TableCell>
              <TableCell>
                <Box component="span" sx={visuallyHidden}>
                  {t('common.actions')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {referrals.map((row) => {
              const firstPaidOn = firstPaidOnOf(row);
              const planId = referralPlanIdOf(row);
              return (
                <TableRow key={row.attributionId ?? row.email}>
                  <TableCell component="th" scope="row">
                    {row.subscriberDisplayName ?? row.email ?? dash}
                  </TableCell>
                  <TableCell>{row.offerName ?? dash}</TableCell>
                  <TableCell>{formatCivilOrDash(row.since, locale, dash)}</TableCell>
                  <TableCell>
                    {formatCivilOrDash(firstPaidOn, locale, dash)}
                  </TableCell>
                  <TableCell>
                    <StatusChip domain="referrals" status={row.status} />
                  </TableCell>
                  <TableCell>
                    {row.source ? enumLabel(t, 'affiliates.referrals', row.source) : dash}
                  </TableCell>
                  <TableCell>{planId === null ? dash : planLabel(planId)}</TableCell>
                  <MoneyCell minor={row.thisPeriodCommissionMinor} currency={row.currency ?? ''} />
                  <TableCell>
                    {isVoided(row) || row.supersededAt || !canWrite ? null : (
                      <Button color="error" size="small" onClick={() => setTarget(row)}>
                        {t('affiliates.referrals.void')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <VoidReferralDialog
        open={target !== null}
        onClose={() => setTarget(null)}
        onVoid={handleVoid}
      />
    </Paper>
  );
}
